import get from 'lodash.get';
import flatMap from 'lodash.flatmap';

/**
 * Get all measures for grade.
 * @param {Array} gradeToMeasureMapper which contains grade and all corresponding measures
 * @param {string} grade is a param for finding all corresponding measures
 */
export const getMeasuresByGrade = (gradeToMeasureMapper, grade) => {
    const measureMapperObject = gradeToMeasureMapper.find(item => item.key === grade);
    return get(measureMapperObject, 'measures', []);
};

/**
 * Get info whether measure is available in grade.
 * @param {Array} gradeToMeasureMapper which contains grade and all corresponding measures
 * @param {string} grade param for finding all corresponding measures
 * @param {string} measure param whether is available in grade
 */
export const isMeasureAvailableInGrade = (gradeToMeasureMapper, grade, measure) =>
    getMeasuresByGrade(gradeToMeasureMapper, grade).includes(measure);

export const getMeasureAvailiabilityStartIndex = (gradeToMeasureMapper, measure) => ({
    measureStartIndex: gradeToMeasureMapper.findIndex(item => item.measures.includes(measure)),
});

/**
 * Get a list of the unique measures, which are applied only to provides grades.
 * @param {Array} gradeToMeasureMapper which contains grade and all corresponding measures
 * @param {Array} measures list of measures object etc {key: 'MEASURE_1, name: 'MEASURE_NAME', title: 'MEASURE_TITLE'}
 * @param {Array} grades list of grades
 */
export const getUniqueMeasuresForGrades = (gradeToMeasureMapper, measures, grades) => {
    const uniqueMeasures = new Set();
    grades.forEach(grade => {
        getMeasuresByGrade(gradeToMeasureMapper, grade).map(measure => uniqueMeasures.add(measure));
    });
    return measures.filter(measure => uniqueMeasures.has(measure.key));
};

/**
 * Get a list of Student Summary measures, which are unique and based on student's grades.
 * @param {Array} gradeToMeasureMapper which contains grade and all corresponding measures
 * @param {Array} measures list of measures
 * @param {Array} summaryMeasures list of Student Summary measures
 * @param {Array} grades list of grades
 */
export const getSummaryMeasuresForGrades = (
    gradeToMeasureMapper,
    measures,
    summaryMeasures,
    grades,
) =>
    getUniqueMeasuresForGrades(gradeToMeasureMapper, measures, grades).filter(measure =>
        summaryMeasures.includes(measure.key),
    );

/**
 * Get a list of accessible measure-period combinations for the provided grades.
 * @param {Array} gradeToMeasureMapper which contains grade and all corresponding measures
 * @param {Array} measures list of measures
 * @param {Array} summaryMeasures list of Student Summary measures
 * @param {Array} toyPeriods list of toy periods
 * @param {Array} grades list of grades
 */
export const getMeasurePeriodCombinationForGrades = (
    gradeToMeasureMapper,
    measures,
    summaryMeasures,
    toyPeriods,
    grades,
) => {
    const summaryMeasuresForGrades = getSummaryMeasuresForGrades(
        gradeToMeasureMapper,
        measures,
        summaryMeasures,
        grades,
    );

    return flatMap(summaryMeasuresForGrades, measure =>
        toyPeriods.map(period => ({ key: measure.key, title: measure.title, period })),
    );
};
